import { CommonModule, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PropertyService } from '../../../_services/properties/property.service';
import { ToastService } from '../../../_services/toast.service';
import { InrShortPipe } from '../../../_pipes/indian-currency.pipe';
import { NumberFormatPipe } from '../../../_pipes/number-format.pipe';
import { CiCalculationResult, CiRecord, CiService, IntelRecord } from '../../../_services/ci.service';

import { LAND_RATE_SOURCE } from '../../ci/services/ci-api.mapper';
import { ciBenchmarkRate } from '../../ci/models/ci-property.model';

// ─── CIP Intelligence Interface ───────────────────────────────────────────────
export interface CipIntelligence {
  trophyTags?: string;
  verdict?: {
    summary?: string;
    badges?: { label: string; color: 'emerald' | 'amber' | 'violet' | 'blue' }[];
  };
  pricing?: {
    lastAvgTransacted?: string;
    last5TransactionsAvg?: string;
    lastTransactedDate?: string;
    udsCurrentRate?: string;
    benchmarkPsf?: string;
    askVsBenchmark?: string;
    askVsBenchmarkDirection?: 'above' | 'below';
  };
  valuation?: {
    adjustedCurrentValue?: string;
    avgRentMonth?: string;
    avgRentPA?: string;
    rentalYield?: string;
    rentalYieldLabel?: string;
    udsSupport?: string;
  };
  strengthIndicators?: {
    label: string;
    widthPercent: number;
    value: string;
    color: 'emerald' | 'amber';
  }[];
  offerGuidance?: {
    suggestedOfferPsf?: string;
    closePsfLow?: string;
    closePsfHigh?: string;
    suggestedOfferValue?: string;
    likelyCloseLow?: string;
    likelyCloseHigh?: string;
  };
  transactions?: {
    source?: string;
    date: string;
    area: string;
    price: string;
    psf: string;
  }[];
}

type TabKey = 'property' | 'intelligence' | 'investment' | 'location';

@Component({
  selector: 'app-details',
  standalone: true,
  imports: [CommonModule, FormsModule, InrShortPipe, NumberFormatPipe, RouterModule, NgIf],
  templateUrl: './details.component-old.html',   // ← must match your actual HTML filename
  styleUrl: './details.component.scss',
  encapsulation: ViewEncapsulation.None,
  // ↓ Default strategy — do NOT use OnPush here
  changeDetection: ChangeDetectionStrategy.Default,
})
export class DetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  // ── Tab state (public so template can read it) ──────────────────────────────
  activeTab: TabKey = 'property';

  readonly tabs: { key: TabKey; label: string }[] = [
    { key: 'property',     label: 'Property'    },
    { key: 'intelligence', label: 'Intelligence' },
    { key: 'investment',   label: 'Investment'   },
    { key: 'location',     label: 'Location'     },
  ];

  cipData: CipIntelligence | null = null;

  // ── Property state ──────────────────────────────────────────────────────────
  property: any = {};
  advisor = signal<any[]>([]);
  selectedImage = '';
  isLoading = true;
  hoveredPriceRow: string | null = null;

  // ── Injected services ───────────────────────────────────────────────────────
  private readonly sanitizer = inject(DomSanitizer);
  private readonly cdr       = inject(ChangeDetectorRef);
  private scrollObserver: IntersectionObserver | null = null;

  readonly furnishingMap = [
    { key: 'wardrobe',       label: 'Wardrobe',       emoji: '🚪' },
    { key: 'modularKitchen', label: 'Modular Kitchen', emoji: '🍳' },
    { key: 'airConditioner', label: 'Air Conditioner', emoji: '💨' },
    { key: 'chimney',        label: 'Chimney',         emoji: '🌫️' },
    { key: 'sofa',           label: 'Sofa',            emoji: '🛋️' },
    { key: 'bed',            label: 'Bed',             emoji: '🛏️' },
    { key: 'fridge',         label: 'Fridge',          emoji: '❄️' },
    { key: 'fan',            label: 'Exhaust Fan',     emoji: '🌀' },
    { key: 'light',          label: 'Lights',          emoji: '💡' },
  ];

  constructor(
    public  _PS: PropertyService,
    private route: ActivatedRoute,
    private _NS: ToastService,
    private router: Router,
    private ciApi: CiService,
  ) {}

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');
      if (id) this.getPropertyDetails(id);
    });
  }

  ngAfterViewInit(): void { /* scroll observer starts after data loads */ }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  setActiveTab(tab: TabKey): void {
    this.activeTab = tab;
    this.cdr.markForCheck()
  }

  // ── Badge helpers ───────────────────────────────────────────────────────────

  getBadgeClass(color: string): string {
    const map: Record<string, string> = {
      emerald: 'border-emerald-400 text-emerald-600',
      amber:   'border-amber-400 text-amber-600',
      violet:  'border-violet-500 text-violet-600',
      blue:    'border-blue-400 text-blue-600',
    };
    return map[color] ?? 'border-slate-300 text-slate-500';
  }

  getBadgeDot(color: string): string {
    const map: Record<string, string> = {
      emerald: 'bg-emerald-500',
      amber:   'bg-amber-400',
      violet:  'bg-violet-500',
      blue:    'bg-blue-400',
    };
    return map[color] ?? 'bg-slate-400';
  }

  // ── Scroll reveal ───────────────────────────────────────────────────────────

  private initScrollReveal(): void {
    this.scrollObserver?.disconnect();
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add('vis'), i * 55);
            this.scrollObserver?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 },
    );
    setTimeout(() => {
      document.querySelectorAll('.rv').forEach((el) => {
        el.classList.remove('vis');
        this.scrollObserver?.observe(el);
      });
    }, 100);
  }

  // ── Data loading ────────────────────────────────────────────────────────────

  getPropertyDetails(id: string): void {
    this.isLoading = true;
    this._PS.getProperty(id).subscribe({
      next: (res: any) => {
        this.property    = res.data ?? {};
        const gallery    = this.property.galleryImage ?? [];
        this.selectedImage = gallery[0] ?? '';
        this.cipData     = this.getCiIntelligence(this.property);
        if (!this.cipData) this.loadGeneratedCi(this.property);
        this.getAllAssignedAdvisorDetails(this.property.assignedAdvisors ?? []);
        this.isLoading   = false;
        // detectChanges() still needed here so the template renders
        // the loaded data before initScrollReveal queries the DOM
        this.cdr.detectChanges();
        this.initScrollReveal();
      },
      error: () => {
        this.isLoading = false;
        this._NS.error('Failed to load property details.');
        this.cdr.detectChanges();
      },
    });
  }

  /** Load a generated CI report when property details only expose a CI id. */
  private loadGeneratedCi(property: any): void {
    const ciId = property?.ciId
      ?? property?.ciRecordId
      ?? property?.ciCalculationId
      ?? property?.intelligenceId
      ?? property?.circledIntelligenceId;

    if (ciId) {
      this.ciApi.getCiRecordById(ciId).subscribe({
        next: (record) => this.applyCiRecord(record),
        error: () => this.loadCiFromDashboard(property),
      });
      return;
    }

    this.loadCiFromDashboard(property);
  }

  /**
   * Older property documents do not yet carry a CI id.  The dashboard still
   * exposes generated records, so use it as a read-only compatibility lookup.
   */
  private loadCiFromDashboard(property: any): void {
    const propertyName = this.normaliseName(property?.name);
    const propertyId = String(property?._id ?? property?.id ?? '');
    if (!propertyName && !propertyId) return;

    // Use the same dashboard request as the CI list. Some deployed API
    // versions do not support the optional `limit` query and return no rows
    // when it is supplied, even though generated records exist.
    this.ciApi.getDashboardData().subscribe({
      next: (dashboard) => {
        const matches = (dashboard.recentIntel ?? []).filter((item) => {
          const candidate = item as typeof item & { propertyName?: string; property_id?: string };
          const ciPropertyId = String(candidate.propertyId ?? candidate.property_id ?? '');
          const ciPropertyName = this.normaliseName(candidate.property ?? candidate.propertyName);
          return (propertyId && ciPropertyId === propertyId)
            || (propertyName && this.isSamePropertyName(ciPropertyName, propertyName));
        });
        // A property may have older drafts as well as a generated CI. Prefer
        // the completed record, otherwise use the newest matching item.
        const match = matches.find((item) => item.status === 'Completed') ?? matches[0];
        if (!match?.id) return;

        // Render immediately from the dashboard summary. This prevents a
        // blank Intelligence tab if a legacy record endpoint is slow or has a
        // partial response; a successful record request below enriches it.
        this.applyDashboardCiSummary(match);
        this.ciApi.getCiRecordById(match.id).subscribe({
          next: (record) => this.applyCiRecord(record),
          error: () => undefined,
        });
      },
      error: () => undefined,
    });
  }

  private applyCiRecord(record: CiRecord): void {
    this.cipData = this.getCiIntelligence({ ciRecord: record });
    this.cdr.detectChanges();
  }

  private applyDashboardCiSummary(record: IntelRecord): void {
    this.cipData = {
      trophyTags: record.verdict || 'Circled Intelligence',
      verdict: {
        summary: record.verdict
          ? `The latest generated CI classifies this property as ${record.verdict}.`
          : 'Generated CI is available for this property.',
        badges: record.verdict ? [{ label: record.verdict, color: 'violet' }] : [],
      },
      pricing: {
        benchmarkPsf: record.benchmarkPSF || undefined,
        lastTransactedDate: this.date(record.lastUpdated),
      },
    };
    this.cdr.detectChanges();
  }

  private normaliseName(value: string | null | undefined): string {
    return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /**
   * Legacy CI dashboard entries were connected to properties by a display name
   * only. Allow a very small spelling difference (for example, "Solitare" vs
   * "Solitaire") but keep matching strict enough to avoid crossing records.
   */
  private isSamePropertyName(first: string, second: string): boolean {
    if (!first || !second) return false;
    if (first === second) return true;
    if (first.includes(second) || second.includes(first)) return true;
    if (Math.abs(first.length - second.length) > 2) return false;

    let previous = Array.from({ length: second.length + 1 }, (_, index) => index);
    for (let row = 1; row <= first.length; row += 1) {
      const current = [row];
      for (let column = 1; column <= second.length; column += 1) {
        current[column] = Math.min(
          current[column - 1] + 1,
          previous[column] + 1,
          previous[column - 1] + (first[row - 1] === second[column - 1] ? 0 : 1),
        );
      }
      previous = current;
    }
    return previous[second.length] <= 2;
  }

  investmentFiveYearRent(): string {
    const annualRent = this.displayMoneyToNumber(this.cipData?.valuation?.avgRentPA)
      ?? (this.displayMoneyToNumber(this.cipData?.valuation?.avgRentMonth) ?? 0) * 12;
    return annualRent ? this.money(annualRent * 5) ?? '—' : '—';
  }

  investmentCloseRange(): string {
    const low = this.cipData?.offerGuidance?.likelyCloseLow;
    const high = this.cipData?.offerGuidance?.likelyCloseHigh;
    return low && high ? `${low} – ${high}` : low || high || '—';
  }

  investmentSavings(): string {
    const ask = this.displayMoneyToNumber(this.property?.price?.[0]?.price ?? this.property?.price?.[0]?.amount);
    const offer = this.displayMoneyToNumber(this.cipData?.offerGuidance?.suggestedOfferValue);
    return ask != null && offer != null && ask > offer ? this.money(ask - offer) ?? '—' : '—';
  }

  investmentMetrics(): { label: string; score: number }[] {
    const yieldValue = this.displayPercentToNumber(this.cipData?.valuation?.rentalYield);
    const askVsBenchmark = this.displayPercentToNumber(this.cipData?.pricing?.askVsBenchmark);
    const askDifference = askVsBenchmark == null ? null : Math.abs(askVsBenchmark);
    const udsSupport = this.displayPercentToNumber(this.cipData?.valuation?.udsSupport);
    return [
      { label: 'Price score', score: this.scoreFromDifference(askDifference) },
      { label: 'Yield score', score: yieldValue == null ? 0 : Math.min(100, Math.round(yieldValue * 20)) },
      { label: 'Market score', score: this.scoreFromDifference(askDifference, 92) },
      { label: 'UDS score', score: udsSupport == null ? 0 : Math.min(100, Math.round(udsSupport * 5)) },
      { label: 'Liquidity score', score: this.scoreFromDifference(askDifference, 86) },
    ];
  }

  private scoreFromDifference(difference: number | null, max = 96): number {
    return difference == null ? 0 : Math.max(0, Math.min(100, Math.round(max - difference * 3)));
  }

  private displayPercentToNumber(value: string | undefined): number | null {
    return value ? this.number(value) : null;
  }

  private displayMoneyToNumber(value: unknown): number | null {
    if (typeof value === 'number') return value;
    if (typeof value !== 'string') return null;
    const amount = this.number(value);
    if (amount == null) return null;
    if (/\bcr\b/i.test(value)) return amount * 10_000_000;
    if (/\bl(ac|akh)?s?\b/i.test(value)) return amount * 100_000;
    return amount;
  }

  /**
   * Property details can contain either the website-ready `cipIntelligence`
   * payload or the CI record produced by the calculator.  Keep the details
   * page tolerant of both shapes while the API is rolled out across existing
   * properties.
   */
  private getCiIntelligence(property: any): CipIntelligence | null {
    const intelligence = property?.cipIntelligence
      ?? property?.ciIntelligence
      ?? property?.ciPublicReport
      ?? property?.ciRecord
      ?? property?.ciCalculation
      ?? property?.ci;

    if (!intelligence || typeof intelligence !== 'object') return null;

    // The consumer-ready payload already has the exact presentation shape.
    if ('pricing' in intelligence || 'offerGuidance' in intelligence) {
      const report = intelligence as CipIntelligence;
      return {
        ...report,
        transactions: Array.isArray(intelligence.lastFiveTransactions)
          ? this.benchmarkRecords(intelligence)
          : (report.transactions ?? [])
              .filter((row) => row && row.source !== LAND_RATE_SOURCE && ciBenchmarkRate({ benchmarkPsf: row.psf }) !== null)
              .slice(0, 5),
      };
    }

    const ci = intelligence as Partial<CiCalculationResult>;
    if (ci.benchmarkPsf == null && ci.revisedBenchmark == null && !ci.adjustedVerdict) {
      return null;
    }

    const benchmark = ci.revisedBenchmark ?? ci.benchmarkPsf;
    // The API returns a ratio ((ask ÷ CI value) − 1); everything below works in percent.
    const askDifference = ci.askVsBenchmarkPercent == null ? undefined : ci.askVsBenchmarkPercent * 100;
    const rentalYield = ci.rentalYield;
    const hasPositiveVerdict = /strong buy|buy|opportunity/i.test(
      `${ci.adjustedVerdict ?? ''} ${ci.circledIntelligenceVerdict ?? ''}`,
    );

    return {
      trophyTags: ci.trophyOverride ? 'Trophy Asset' : undefined,
      verdict: {
        summary: ci.verdictDescription || ci.circledIntelligenceVerdict || ci.adjustedVerdict || 'CI analysis is available for this property.',
        badges: [
          ci.adjustedVerdict ? {
            label: ci.adjustedVerdict,
            color: hasPositiveVerdict ? 'emerald' as const : 'amber' as const,
          } : null,
          rentalYield != null ? {
            label: `Rental yield ${this.percent(rentalYield)}`,
            color: 'emerald' as const,
          } : null,
        ].filter((badge): badge is NonNullable<typeof badge> => badge !== null),
      },
      pricing: {
        lastAvgTransacted: this.psf(ci.lastTransactionPsf),
        last5TransactionsAvg: this.psf(ci.last5TransactionsAvgPsf),
        lastTransactedDate: this.date(ci.dateOfLastTransactedPrice),
        udsCurrentRate: this.psf(ci.currentUdsRatePerSqft),
        benchmarkPsf: this.psf(benchmark),
        askVsBenchmark: askDifference == null ? undefined : this.percent(Math.abs(askDifference)),
        askVsBenchmarkDirection: askDifference != null && askDifference < 0 ? 'below' : 'above',
      },
      valuation: {
        adjustedCurrentValue: this.money(ci.adjustedCurrentValueOfUds),
        avgRentMonth: this.money(ci.averageRentPerMonth),
        avgRentPA: ci.averageRentPa == null ? undefined : `${this.money(ci.averageRentPa)} / year`,
        rentalYield: rentalYield == null ? undefined : this.percent(rentalYield),
        rentalYieldLabel: rentalYield == null ? undefined : rentalYield >= 4 ? 'Strong rental return' : 'Rental return',
        udsSupport: ci.udsSupportPercent == null ? undefined : this.percent(ci.udsSupportPercent),
      },
      strengthIndicators: this.getStrengthIndicators(ci, askDifference),
      offerGuidance: {
        suggestedOfferPsf: this.psf(ci.suggestedOfferPsf),
        closePsfLow: this.psf(ci.closePsfLow),
        closePsfHigh: this.psf(ci.closePsfHigh),
        suggestedOfferValue: this.money(ci.suggestedOfferValue),
        likelyCloseLow: this.money(ci.likelyCloseValueLow),
        likelyCloseHigh: this.money(ci.likelyCloseValueHigh),
      },
      transactions: this.benchmarkRecords(ci),
    };
  }

  private benchmarkRecords(ci: Partial<CiCalculationResult>): CipIntelligence['transactions'] {
    return (ci.lastFiveTransactions ?? [])
      .filter((row) => row?.source !== LAND_RATE_SOURCE && Number.isFinite(Number(row?.price)) && Number(row?.price) > 0)
      .slice(0, 5)
      .map((row) => ({
        source: row.source?.trim() || '—',
        date: this.date(row.date) ?? '—',
        psf: this.psf(Number(row.price)) ?? '—',
        // A benchmark rate does not establish the comparable's area or sale price.
        area: '—',
        price: '—',
      }));
  }

  private getStrengthIndicators(ci: Partial<CiCalculationResult>, askDifference?: number): CipIntelligence['strengthIndicators'] {
    const items: CipIntelligence['strengthIndicators'] = [];
    if (ci.rentalYield != null) {
      items.push({
        label: 'Rental strength',
        value: this.percent(ci.rentalYield),
        widthPercent: Math.min(100, Math.max(12, ci.rentalYield * 20)),
        color: 'emerald',
      });
    }
    if (askDifference != null) {
      items.push({
        label: 'Ask vs benchmark',
        value: `${askDifference >= 0 ? '+' : ''}${this.percent(askDifference)}`,
        widthPercent: Math.min(100, Math.max(12, 100 - Math.abs(askDifference) * 6)),
        color: askDifference <= 5 ? 'emerald' : 'amber',
      });
    }
    if (ci.udsSupportPercent != null) {
      items.push({
        label: 'Land-value support',
        value: this.percent(ci.udsSupportPercent),
        widthPercent: Math.min(100, Math.max(12, ci.udsSupportPercent * 5)),
        color: 'emerald',
      });
    }
    return items;
  }

  private psf(value: number | string | null | undefined): string | undefined {
    const number = this.number(value);
    return number == null ? undefined : `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(number)}`;
  }

  private money(value: number | string | null | undefined): string | undefined {
    const number = this.number(value);
    if (number == null) return undefined;
    if (Math.abs(number) >= 10_000_000) return `₹${(number / 10_000_000).toFixed(2).replace(/\.00$/, '')} Cr`;
    if (Math.abs(number) >= 100_000) return `₹${(number / 100_000).toFixed(2).replace(/\.00$/, '')} L`;
    return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(number)}`;
  }

  private percent(value: number): string {
    return `${Number(value.toFixed(1))}%`;
  }

  private date(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    }).format(parsed);
  }

  private number(value: number | string | null | undefined): number | null {
    if (value == null || value === '') return null;
    const parsed = typeof value === 'number' ? value : Number(value.replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  getAllAssignedAdvisorDetails(advisorDetails: any[]): void {
    if (!advisorDetails?.length) { this.advisor.set([]); return; }
    this._PS.getAllAssignedAdvisor(advisorDetails).subscribe({
      next: (res: any) => this.advisor.set(res.data ?? []),
      error: ()         => this.advisor.set([]),
    });
  }

  // ── Image helpers ───────────────────────────────────────────────────────────

  onSelectImage(img: string): void {
    this.selectedImage = img;
  }

  onHeroImageError(): void {
    const gallery = this.property?.galleryImage ?? [];
    const nextImage = gallery.find((image: string) => image && image !== this.selectedImage);
    this.selectedImage = nextImage ?? '';
  }

  onDeleteImage(propertyId: string, imageUrl: string, type: string): void {
    const typeMapping: Record<string, 'planImage' | 'galleryImage' | 'propertyVideo'> = {
      plan: 'planImage', gallery: 'galleryImage', video: 'propertyVideo',
    };
    const imageType = typeMapping[type];
    if (!imageType) return;

    if (confirm('Are you sure you want to delete this image?')) {
      this._PS.deleteImage(propertyId, imageUrl, imageType).subscribe({
        next: (response: any) => {
          this._NS.success(response.message);
          const remaining = (this.property.galleryImage ?? []).filter(
            (img: string) => img !== imageUrl,
          );
          this.property.galleryImage = remaining;
          this.selectedImage = remaining[0] ?? '';
        },
        error: (err: any) => this._NS.error(err?.error?.message ?? 'Failed to delete image.'),
      });
    }
  }

  // ── Map helpers ─────────────────────────────────────────────────────────────

  getSafeUrl(locationName: string): SafeResourceUrl {
    const url = `https://maps.google.com/maps?q=${encodeURIComponent(locationName)}&t=&z=13&ie=UTF8&iwloc=&output=embed`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getFormattedAddress(property: any): string {
    return [property.address, property.streetName, property.cityName, property.stateName, property.pinCode]
      .map((p) => p?.toString().trim().replace(/,+$/, ''))
      .filter(Boolean)
      .join(', ');
  }

  // ── Amenities ───────────────────────────────────────────────────────────────

  getVisibleAmenities(propertyAmenities: any): any[] {
    if (!propertyAmenities) return [];
    return Object.keys(propertyAmenities)
      .filter((key) => propertyAmenities[key] === true)
      .map((key) => {
        const iconData = this._PS.amenities?.find((a: any) => a.value === key);
        return { key, label: iconData?.label ?? this.formatKey(key), icon: iconData?.path ?? 'M12 9v6m3-3H9' };
      });
  }

  private formatKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  // ── Rooms ───────────────────────────────────────────────────────────────────

  getRoomIcon(room: string): string {
    const icons: Record<string, string> = {
      'Servant Room': 'fa-solid fa-user-tie',
      'Study Room':   'fa-solid fa-book-open',
      'Store Room':   'fa-solid fa-boxes-stacked',
      'Media Room':   'fa-solid fa-film',
      'Pooja Room':   'fa-solid fa-om',
      'Guest Room':   'fa-solid fa-bed',
    };
    return icons[room] ?? 'fa-solid fa-door-open';
  }

  // ── Furnishing ──────────────────────────────────────────────────────────────

  getDisplayList(furnishingDetails: any): any[] {
    if (!furnishingDetails) return [];
    return this.furnishingMap
      .filter((item) => {
        const val = furnishingDetails[item.key];
        return val === true || (typeof val === 'number' && val > 0);
      })
      .map((item) => {
        const val = furnishingDetails[item.key];
        return { emoji: item.emoji, text: typeof val === 'number' ? `${val} ${item.label}` : item.label };
      });
  }

  // ── Structure ───────────────────────────────────────────────────────────────

  formatBuildingStr(input: any): string {
    if (!input) return '—';
    const parts = input.trim().replace(/[,+]+/g, ' ').replace(/\s+/g, ' ')
      .toUpperCase().split(' ').filter((p: string) => p.length > 0);
    return parts.length === 0 ? '—' : parts.join(' + ');
  }

  // ── Advisor navigation ──────────────────────────────────────────────────────

  viewProfile(advisor: any): void {
    this.router.navigate([`admin/advisor/profile/${advisor.name}/${advisor._id}`]);
  }

  // ── Price helpers ───────────────────────────────────────────────────────────

  getPriceStructureBadgeClass(type: string): Record<string, boolean> {
    const t = (type ?? '').toLowerCase();
    return {
      'price-badge-simplex':   t === 'simplex',
      'price-badge-duplex':    t === 'duplex',
      'price-badge-penthouse': t === 'penthouse',
      'price-badge-default':   !['simplex', 'duplex', 'penthouse'].includes(t),
    };
  }

  getMinPrice(): number {
    const prices = (this.property.price ?? []).map((p: any) => Number(p.price)).filter(Number.isFinite);
    return prices.length ? Math.min(...prices) : 0;
  }

  getMaxPrice(): number {
    const prices = (this.property.price ?? []).map((p: any) => Number(p.price)).filter(Number.isFinite);
    return prices.length ? Math.max(...prices) : 0;
  }

  getPriceBarWidth(price: number): number {
    const max = this.getMaxPrice();
    const min = this.getMinPrice();
    return max === min ? 100 : Math.round(((price - min) / (max - min)) * 100);
  }

  getPropertyPriceDisplay(priceArray: any[]): any {
    if (!priceArray?.length) return 'Price on Request';
    const nums = priceArray.map((p) => Number(p.price)).filter(Number.isFinite);
    if (!nums.length) return 'Price on Request';
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    return min === max ? min : { min, max, isRange: true };
  }
}
